import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";
import { pickCachePreset, requestHasAuthCookie } from "@/lib/cache-headers";

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

// Bot / datacenter traffic firewall.
// Ye site Pakistan/India/Gulf/US audience ke liye hai — CN se koi
// legitimate audience nahi hai, aur pichle dino CN + SG datacenter IPs se
// bot spike aa raha tha (2s dwell, high bounce, analytics polluter).
// Yahan hum edge par hi 403 return karte hain, taki:
//   - AdSense impressions waste na hon (invalid traffic risk)
//   - GA/Lovable analytics polluted na ho (script hi load nahi hoga)
//   - Origin CPU aur bandwidth bache
// Agar future me China ke real users chahiye to `BLOCKED_COUNTRIES` se
// "CN" hata dena — koi aur code change ki zaroorat nahi.
const BLOCKED_COUNTRIES = new Set(["CN"]);

// ---------------------------------------------------------------------------
// Canonical host redirect (SEO consolidation)
// The project is also reachable on its Lovable domain (gold-rate.lovable.app).
// Do NOT let that host get indexed as a separate site — Google/Bing would treat
// it as duplicate content and split ranking signals with drhint.com.
// Instead we 301 every Lovable production URL to the SAME path on drhint.com,
// so any Lovable link that gets crawled or shared consolidates into drhint.com
// and every visitor lands on the real website.
// Preview / dev hosts are excluded so the Lovable editor keeps working, and
// /api/public/* is excluded so webhooks + cron using the stable Lovable URLs
// keep functioning.
const CANONICAL_HOST = "drhint.com";
const canonicalHostMiddleware = createMiddleware().server(async ({ request, next }) => {
  try {
    const url = new URL(request.url);
    const host = url.hostname.toLowerCase();
    const isLovableHost = host.endsWith(".lovable.app");
    const isPreviewHost = host.includes("-preview--") || host.endsWith("-dev.lovable.app");
    const isPublicApi = url.pathname.startsWith("/api/public/");
    if (isLovableHost && !isPreviewHost && !isPublicApi) {
      const target = `https://${CANONICAL_HOST}${url.pathname}${url.search}`;
      return new Response(null, {
        status: 301,
        headers: {
          Location: target,
          "Cache-Control": "public, max-age=3600, s-maxage=86400",
          "x-redirect-reason": "canonical-host",
        },
      });
    }
  } catch {
    /* never break request for redirect errors */
  }
  return next();
});

const botFirewallMiddleware = createMiddleware().server(async ({ request, next }) => {
  try {
    const country = (request.headers.get("cf-ipcountry") || "").toUpperCase();
    if (BLOCKED_COUNTRIES.has(country)) {
      return new Response("Forbidden", {
        status: 403,
        headers: {
          "content-type": "text/plain; charset=utf-8",
          "cache-control": "public, max-age=0, s-maxage=86400",
          "x-block-reason": "geo-datacenter-filter",
        },
      });
    }
    const ua = (request.headers.get("user-agent") || "").toLowerCase();
    // Known bad automation UAs (headless scrapers). Search-engine crawlers
    // (Googlebot / Bingbot / etc.) intentionally NOT included.
    if (
      ua === "" ||
      /headlesschrome|phantomjs|puppeteer|playwright|selenium|python-requests|curl\/|wget\/|go-http-client|okhttp|scrapy|httpclient|axios\//i.test(ua)
    ) {
      return new Response("Forbidden", {
        status: 403,
        headers: {
          "content-type": "text/plain; charset=utf-8",
          "cache-control": "no-store",
          "x-block-reason": "ua-automation-filter",
        },
      });
    }
  } catch {
    /* never break request for firewall errors */
  }
  return next();
});

// Edge cache middleware — sets Cache-Control on GET responses based on
// pathname so the CDN can serve subsequent hits without touching origin.
// Skips authenticated requests, non-GET, redirects, and responses that
// already set Cache-Control or Set-Cookie.
const edgeCacheMiddleware = createMiddleware().server(async ({ request, pathname, next }) => {
  const result = await next();
  try {
    const method = request.method.toUpperCase();
    if (method !== "GET" && method !== "HEAD") return result;

    const res = result.response;
    if (!res || res.status >= 300) return result;
    if (res.headers.has("Set-Cookie")) return result;
    if (res.headers.has("Cache-Control")) return result;
    if (requestHasAuthCookie(request)) return result;

    const preset = pickCachePreset(pathname);
    if (!preset) return result;

    res.headers.set("Cache-Control", preset);
    res.headers.set("CDN-Cache-Control", preset);
    res.headers.set("Cloudflare-CDN-Cache-Control", preset);
  } catch {
    /* never break the response for caching */
  }
  return result;
});


// Googlebot / Bingbot crawl logger — our own "server log" for crawl-budget
// analysis (the edge host keeps no raw access logs). Only search-engine bot
// requests are recorded; normal visitors are untouched.
const BOT_RE = /(googlebot|google-inspectiontool|googleother|bingbot|adsbot-google|mediapartners-google)/i;
const botLogMiddleware = createMiddleware().server(async ({ request, next }) => {
  const ua = request.headers.get("user-agent") || "";
  const m = BOT_RE.exec(ua);
  if (!m) return next();
  const t0 = Date.now();
  const result = await next();
  try {
    const url = new URL(request.url);
    if (!url.pathname.startsWith("/assets/")) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await (supabaseAdmin as any).from("bot_crawl_log").insert({
        bot: m[1].toLowerCase(),
        path: url.pathname.slice(0, 500),
        query: url.search ? url.search.slice(0, 300) : null,
        status: result.response?.status ?? 200,
        ms: Date.now() - t0,
        country: request.headers.get("cf-ipcountry"),
      });
    }
  } catch {
    /* logging must never break crawling */
  }
  return result;
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [errorMiddleware, botLogMiddleware, canonicalHostMiddleware, botFirewallMiddleware, edgeCacheMiddleware],
}));
